import { useEffect, useRef, useState } from "react";
import { FiPlus, FiRefreshCw, FiTrash2, FiSave, FiGlobe } from "react-icons/fi";
import axios from "axios";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import API_BASE_URL from "../config";
import Chart from "chart.js/auto";
import "../styles/SystemMonitoring.css";

export default function SystemMonitoring() {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    // eslint-disable-next-line no-unused-vars
    const [targets, setTargets] = useState([]);
    const [selectedTarget, setSelectedTarget] = useState(null);

    const [newUrl, setNewUrl] = useState("");
    const [newName, setNewName] = useState("");

    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pingLoading, setPingLoading] = useState(false);

    /* -----------------------------
       Load all saved websites
    ------------------------------*/
    const loadTargets = async () => {
        try {
            const res = await axios.get(
                `${API_BASE_URL}/api/monitoring/targets`
            );

            const list = res.data.data || [];
            setTargets(list);

            if (list.length) {
                setSelectedTarget(list[0]);
                setNewName(list[0].name);
                setNewUrl(list[0].url);
            } else {
                toast.info("No monitoring targets found. Add a new website to start monitoring.");
            }

        } catch (err) {
            console.error(err);
            toast.error("Failed to load monitoring targets");
        }
    };

    /* -----------------------------
       Add or Update website
    ------------------------------*/
    const saveTarget = async (e) => {
        e.preventDefault();

        if (!newName.trim() || !newUrl.trim()) {
            toast.warning("Please enter both site name and URL");
            return;
        }

        try {
            setSaving(true);

            if (selectedTarget?._id) {
                // Update
                await axios.put(
                    `${API_BASE_URL}/api/monitoring/targets/${selectedTarget._id}`,
                    {
                        name: newName.trim(),
                        url: newUrl.trim()
                    }
                );
                toast.success("Website updated successfully!");
            } else {
                // Add new
                await axios.post(
                    `${API_BASE_URL}/api/monitoring/targets`,
                    {
                        name: newName.trim(),
                        url: newUrl.trim()
                    }
                );
                toast.success("Website added successfully!");
            }

            await loadTargets();

        } catch (err) {
            console.error(err);
            toast.error("Failed to save website");
        } finally {
            setSaving(false);
        }
    };

    /* -----------------------------
       Load logs
    ------------------------------*/
    const loadLogs = async (targetId) => {
        if (!targetId) return;

        try {
            setLoading(true);

            const res = await axios.get(
                `${API_BASE_URL}/api/monitoring/targets/${targetId}/logs`
            );

            setLogs(res.data.data || []);

        } catch (err) {
            console.error(err);
            toast.error("Failed to load monitoring logs");
        } finally {
            setLoading(false);
        }
    };

    /* -----------------------------
       Clear logs with SweetAlert
    ------------------------------*/
    const clearLogs = async () => {
        if (!selectedTarget?._id || logs.length === 0) return;

        const result = await Swal.fire({
            title: 'Clear All Logs?',
            text: `This will permanently delete all ping logs for "${selectedTarget.name}". This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Yes, clear all logs',
            cancelButtonText: 'Cancel',
            background: '#ffffff',
            color: '#1e293b',
            customClass: {
                popup: 'devopstrio-swal-popup'
            }
        });

        if (!result.isConfirmed) return;

        try {
            await axios.delete(
                `${API_BASE_URL}/api/monitoring/targets/${selectedTarget._id}/logs`
            );

            setLogs([]);

            if (chartInstance.current) {
                chartInstance.current.destroy();
                chartInstance.current = null;
            }

            toast.success("All logs cleared successfully!");
        } catch (err) {
            console.error(err);
            toast.error("Failed to clear logs");
        }
    };

    /* -----------------------------
       Ping now button
    ------------------------------*/
    const runPingNow = async () => {
        if (!selectedTarget?._id) {
            toast.warning("Please select or add a website first");
            return;
        }

        try {
            setPingLoading(true);

            await axios.post(
                `${API_BASE_URL}/api/monitoring/targets/${selectedTarget._id}/ping`
            );

            await loadLogs(selectedTarget._id);
            toast.success("Ping executed successfully!");

        } catch (err) {
            console.error(err);
            toast.error("Ping failed");
        } finally {
            setPingLoading(false);
        }
    };

    useEffect(() => {
        loadTargets();
    }, []);

    useEffect(() => {
        if (!selectedTarget?._id) return;

        setNewName(selectedTarget.name);
        setNewUrl(selectedTarget.url);

        loadLogs(selectedTarget._id);

    }, [selectedTarget]);

    /* -----------------------------
       Chart
    ------------------------------*/
    useEffect(() => {

        if (!logs.length) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
                chartInstance.current = null;
            }
            return;
        }

        const labels = logs.map(l =>
            new Date(l.checkedAt).toLocaleTimeString()
        );

        const data = logs.map(l => l.responseTimeMs);

        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        chartInstance.current = new Chart(chartRef.current, {
            type: "line",
            data: {
                labels,
                datasets: [
                    {
                        label: "Response Time (ms)",
                        data,
                        borderColor: "#3b82f6",
                        backgroundColor: "rgba(59, 130, 246, 0.1)",
                        tension: 0.3,
                        borderWidth: 2,
                        fill: true
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: true },
                    tooltip: {
                        mode: 'index',
                        intersect: false
                    }
                },
                scales: {
                    y: {
                        title: { display: true, text: "Milliseconds" },
                        beginAtZero: true
                    },
                    x: { title: { display: true, text: "Time" } }
                }
            }
        });

    }, [logs]);

    return (
        <div className="system-monitoring-container">
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />

            {/* ---------- Inputs and buttons form ---------- */}
            <form className="monitoring-form" onSubmit={saveTarget}>
                <div className="form-input-group">
                    <div className="input-wrapper">
                        <FiGlobe className="input-icon" />
                        <input
                            type="text"
                            placeholder="Site name"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            className="monitoring-input"
                        />
                    </div>
                </div>

                <div className="form-input-group">
                    <div className="input-wrapper">
                        <FiGlobe className="input-icon" />
                        <input
                            type="url"
                            placeholder="https://example.com"
                            value={newUrl}
                            onChange={(e) => setNewUrl(e.target.value)}
                            className="monitoring-input"
                        />
                    </div>
                </div>

                <button
                    type="submit"
                    className="monitoring-btn save-btn"
                    disabled={saving}
                >
                    {saving ? (
                        <>
                            <FiRefreshCw className="btn-icon spinning" />
                            Saving...
                        </>
                    ) : selectedTarget ? (
                        <>
                            <FiSave className="btn-icon" />
                            Update
                        </>
                    ) : (
                        <>
                            <FiPlus className="btn-icon" />
                            Add
                        </>
                    )}
                </button>

                <button
                    type="button"
                    className="monitoring-btn ping-btn"
                    onClick={runPingNow}
                    disabled={!selectedTarget || pingLoading}
                >
                    {pingLoading ? (
                        <>
                            <FiRefreshCw className="btn-icon spinning" />
                            Pinging...
                        </>
                    ) : (
                        <>
                            <FiRefreshCw className="btn-icon" />
                            Ping Now
                        </>
                    )}
                </button>
            </form>

            {/* ---------------- Chart ---------------- */}
            <div className="chart-container">
                {loading && (
                    <div className="loading-state">
                        <FiRefreshCw className="loading-icon spinning" />
                        <p>Loading monitoring data...</p>
                    </div>
                )}

                {!loading && selectedTarget && logs.length === 0 && (
                    <div className="empty-state">
                        <FiGlobe className="empty-icon" />
                        <h3>No Monitoring Data</h3>
                        <p>Click "Ping Now" to start monitoring this website</p>
                    </div>
                )}

                <div className="chart-wrapper">
                    <canvas ref={chartRef}></canvas>
                </div>
            </div>

            {/* ---------------- Log table ---------------- */}
            {logs.length > 0 && (
                <div className="logs-container">
                    <div className="logs-header">
                        <h3>Monitoring Logs</h3>
                        <button
                            type="button"
                            className="clear-btn"
                            onClick={clearLogs}
                            disabled={!selectedTarget || logs.length === 0}
                        >
                            <FiTrash2 className="btn-icon" />
                            Clear All Logs
                        </button>
                    </div>

                    <div className="table-wrapper">
                        <table className="monitoring-table">
                            <thead>
                                <tr>
                                    <th>Time</th>
                                    <th>Status</th>
                                    <th>Response (ms)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.slice().reverse().map((l) => (
                                    <tr key={l._id}>
                                        <td>{new Date(l.checkedAt).toLocaleString()}</td>
                                        <td className={`status-cell ${l.status.toLowerCase()}`}>
                                            {l.status}
                                        </td>
                                        <td>{l.responseTimeMs ?? "-"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}